import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Clock, Eye, Loader2, RefreshCw } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { apiService } from "@/lib/api-service";
import { useStrategyStatus } from "@/hooks/use-strategy";

export const Route = createFileRoute("/_app/_/onboarding/generating")({
	component: GeneratingPage,
});

function GeneratingPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { data: strategyStatus } = useStrategyStatus();

	// Only redirect once the strategy is fully approved and ready.
	// Do NOT redirect on "in_review" or "failed" — the app loader still has
	// onboarding_step="generating" at that point and would force an immediate
	// redirect back here, creating a navigation loop. Instead, render each
	// state inline so the user always has a path forward.
	useEffect(() => {
		if (strategyStatus?.status === "ready") {
			navigate({ to: "/strategy" });
		}
	}, [strategyStatus, navigate]);

	const retryMutation = useMutation({
		mutationFn: () => apiService.retryStrategyGeneration(),
		onSuccess: () => {
			toast.success("Strategy generation restarted.");
			queryClient.invalidateQueries({ queryKey: ["strategy-status"] });
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to retry strategy generation.");
		},
	});

	const status = strategyStatus?.status;
	const isFailed = status === "failed";
	const isInReview = status === "in_review";

	// Failed state — render inline retry so the user isn't stuck.
	// (Navigating to /strategy is blocked by the app loader while
	// onboarding_step is still "generating".)
	if (isFailed) {
		return (
			<div className="h-dvh flex flex-col items-center justify-center px-6">
				<div className="w-full max-w-md text-center space-y-8">
					<div className="flex items-center justify-center">
						<div className="h-20 w-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center animate-in zoom-in duration-500">
							<AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
						</div>
					</div>
					<div className="space-y-3">
						<h1 className="text-3xl font-semibold text-foreground tracking-tight">
							Strategy Generation Failed
						</h1>
						<p className="text-muted-foreground">
							Something went wrong while generating your campaign strategy.
							This can happen due to temporary service issues. Please try again.
						</p>
					</div>
					<Button
						onClick={() => retryMutation.mutate()}
						disabled={retryMutation.isPending}
					>
						{retryMutation.isPending ? (
							<Loader2 className="size-4 animate-spin mr-2" />
						) : (
							<RefreshCw className="size-4 mr-2" />
						)}
						Try Again
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="h-dvh flex flex-col items-center justify-center px-6">
			<div className="w-full max-w-md text-center space-y-8">
				{/* Icon */}
				<div className="flex items-center justify-center">
					<div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-in zoom-in duration-500">
						{isInReview ? (
							<Eye className="h-10 w-10 text-green-600 dark:text-green-400" />
						) : (
							<CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
						)}
					</div>
				</div>

				{/* Content */}
				<div className="space-y-3">
					<h1 className="text-3xl font-semibold text-foreground tracking-tight">
						{isInReview
							? "Strategy Under Review"
							: "Your Strategy is Being Built"}
					</h1>
					<p className="text-muted-foreground">
						{isInReview
							? "Your strategy has been generated and is now being reviewed by our team before delivery."
							: "Our team is reviewing your responses and building your personalized campaign strategy."}
					</p>
				</div>

				{/* Timeline card */}
				<Card className="border-border/50">
					<CardContent className="p-4 flex items-center gap-3">
						<Clock className="h-5 w-5 text-muted-foreground shrink-0" />
						<p className="text-sm text-muted-foreground text-left">
							Expected by the next business day. We'll notify you when it's
							ready.
						</p>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
